// Type-level mirror of the Pydantic response models in
// ``Code/api/models.py`` (Phase 3.1). Keep them in sync — Phase 3.3+
// new fields land here first, then in the route handlers.

export type JobState = 'pending' | 'submitted' | 'done' | 'failed' | 'cancelled';

/** Phase 3.13.2 + 3.16 v2 — per-catalog residual record.
 *  See ``api.models.JobResidualSchema``. Pre-3.16v2 the only
 *  fields surfaced were ``catalog`` + ``dr_3d_mm`` + the quality
 *  flags; 3.16 v2 added per-axis NEU + ``comparison_source`` so
 *  the DetailScreen can render the full breakdown with the
 *  methodology label. */
export interface JobResidual {
  catalog: string; // "IGS_DAILY" post-3.16v2; "ITRF2014"/"ITRF2020" legacy.
  dr_3d_mm: number;
  /** Phase 3.16 v2 — per-axis ENU residuals in millimetres. */
  dn_mm: number;
  de_mm: number;
  du_mm: number;
  /** Phase 4.6 — per-axis ECEF (geocentric) residuals in millimetres.
   *  Surfaced so the DetailScreen ResidualBlock ENU↔XYZ toggle can
   *  flip between geodetic (dN/dE/dU) and geocentric (dX/dY/dZ)
   *  breakdowns. |dR| stays identical between the two views. */
  dx_mm: number;
  dy_mm: number;
  dz_mm: number;
  data_quality_warning: boolean;
  quality_reason: string | null;
  /** Phase 3.16 v2 — methodology label.
   *  ``"igs_daily_sinex"`` — new path, direct comparison.
   *  ``"itrf2014_propagated"`` — pre-3.16v2 legacy rows. */
  comparison_source: string;
  /** Phase 4.6 — IGS Daily Combined SINEX provenance. ECEF metres
   *  extracted from the SINEX ``+SOLUTION/ESTIMATE`` block. NULL on
   *  legacy ``itrf2014_propagated`` rows (that methodology never
   *  had an IGS coordinate triple to record). The DetailScreen
   *  "эталон" card renders ONLY when these three are non-null. */
  igs_x_m: number | null;
  igs_y_m: number | null;
  igs_z_m: number | null;
  /** Phase 4.6 — ISO YYYY-MM-DD; the date of the SINEX file we
   *  actually read. EQUAL to ``obs_date`` for the normal case;
   *  DIFFERENT (within ±5 days) when the Phase-4.6 neighbour-day
   *  fallback engaged. Mini App keys the ``FALLBACK`` badge on
   *  ``igs_sinex_date !== job.obs_date``. */
  igs_sinex_date: string | null;
  /** Phase G.4 — PPP service-computed ECEF metres. Already on the
   *  JobResidual model since Phase A; surfaced on the wire so the
   *  DetailScreen can render «ВЫЧИСЛЕНО (сервис)» next to «ЭТАЛОН»
   *  for build-quality verification (parser sanity check). */
  service_x_m: number | null;
  service_y_m: number | null;
  service_z_m: number | null;
}

export interface JobSummary {
  id: number;
  name: string | null;
  station_code: string | null;
  obs_date: string | null; // ISO date "YYYY-MM-DD"
  gnss_systems: string[]; // e.g. ["gps", "glonass"]
  state: JobState;
  tag: string | null;
  created_at: string; // ISO datetime
  submitted_at: string | null;
  finished_at: string | null;
  /** Max 3-D residual magnitude across all per-catalog rows (DONE only). */
  residual_3d_mm: number | null;
  /** Scheduler-internal markers stripped, truncated to ≤80 chars (FAILED only). */
  error_summary: string | null;
  /** Phase 3.13.2: OR across all residuals attached to this job.
   *  ``true`` ⇒ FeedScreen renders "⚠ аномалия" instead of the
   *  unreadable km-scale dR (catalogue-collision case, P117 / P101). */
  has_quality_warning: boolean;
  /** Phase 3.15: task is kept on PPP for diagnostics (compute_failed
   *  case, e.g. WHIT × BDS-only). FeedScreen / JobCard renders a small
   *  "🔬 диагностика" badge so the operator can spot these at a glance. */
  preserve_on_ppp: boolean;
  /** Phase 4.1: outcome of the pre-flight RINEX header check. NULL
   *  for jobs older than 4.1. The Mini App reads this to decide
   *  whether to render the subset / incompatible plate on DetailScreen
   *  and (later) a small badge on the Feed card. */
  rinex_validation_status?: RinexValidationStatus | null;
  /** Phase R: operator-facing failure bucket stamped on terminal
   *  jobs. NULL on PENDING/SUBMITTED forever, NULL on terminal
   *  jobs that pre-date the backfill — UI treats NULL as
   *  «category unknown» and falls back to legacy state colouring. */
  failure_category?: FailureCategoryR | null;
  /** Wave 4b — lifted from JobDetail to the summary so the FEED can
   *  render «○ нет эталона» on a DONE job that produced no residual
   *  because no IGS reference existed in the ±5-day window. NULL for
   *  the normal case (residual exists) and for jobs predating 4.6.
   *  See {@link ResidualSkipReason}. */
  residual_skip_reason?: ResidualSkipReason | null;
  /** P1 · 2B — raw residual-stage outcome (for analysis) + operator-facing
   *  failure code. The UI keys off {@link residual_display}, not these. */
  residual_outcome?: string | null;
  residual_failure_code?: string | null;
  /** P1 · 2B — the NORMALIZED render class, computed server-side by the
   *  canonical Python helper. The UI maps a class → localized text/glyph and
   *  never re-derives the domain rules. See {@link ResidualDisplay}. */
  residual_display?: ResidualDisplay;
}

/** Phase R — five-bucket failure taxonomy. Keep in sync with the
 *  backend enum ``analytics.failed_classification.FailureCategoryR``. */
export type FailureCategoryR =
  | 'not_applicable'
  | 'service_failed'
  | 'no_reference'
  | 'file_unavailable'
  | 'other';

/** Phase R — short operator-readable label per category. Russian
 *  per spec (terms are designer-final and locked). */
export const FAILURE_CATEGORY_R_LABEL: Record<FailureCategoryR, string> = {
  not_applicable: 'не применимо',
  service_failed: 'не вычислено',
  no_reference: 'нет эталона',
  file_unavailable: 'файл недоступен',
  other: 'прочее',
};

/** Phase R — longer explanatory subtitle per category. Used on
 *  the job card under the short label. */
export const FAILURE_CATEGORY_R_SUBLABEL: Record<FailureCategoryR, string> = {
  not_applicable: 'станция не пишет систему',
  service_failed: 'сервис не сошёлся',
  no_reference: 'нет опорных координат',
  file_unavailable: 'не получен',
  other: 'расследовать позже',
};

/** Phase R — only `service_failed` is a real failure that warrants
 *  the accent colour. Everything else is neutral («expected» or
 *  «infrastructure / needs investigation»). */
export const FAILURE_CATEGORY_R_IS_REAL_FAILURE: Record<
  FailureCategoryR,
  boolean
> = {
  not_applicable: false,
  service_failed: true,
  no_reference: false,
  file_unavailable: false,
  other: false,
};

/** C4a — runtime list of the R machine keys, the single source paired with the
 *  {@link FailureCategoryR} union above. ``satisfies`` forbids an invalid/extra
 *  member; the compile-time check below forbids a MISSING one — so this can
 *  never silently desync into a second, unsynchronized dictionary. */
export const FAILURE_CATEGORY_R_VALUES = [
  'not_applicable',
  'service_failed',
  'no_reference',
  'file_unavailable',
  'other',
] as const satisfies readonly FailureCategoryR[];

// A new union member not added to the array makes `_MissingRValue` a real key,
// and `true` can no longer be assigned to it → the build fails until synced.
type _MissingRValue = Exclude<FailureCategoryR, (typeof FAILURE_CATEGORY_R_VALUES)[number]>;
const _assertNoMissingRValue: [_MissingRValue] extends [never] ? true : _MissingRValue = true;
void _assertNoMissingRValue;

/** C4a — runtime guard for ``?failure_category=`` at the Feed's input boundary.
 *  An unknown / obsolete key (stale deep-link, old bookmark, hand-edited URL) is
 *  rejected here so it never enters the filter state, the jobs request or the UI.
 *  Validates the stable MACHINE key, never a localized label. */
export function isFailureCategoryR(value: string): value is FailureCategoryR {
  return (FAILURE_CATEGORY_R_VALUES as readonly string[]).includes(value);
}

/** Phase 4.1 — vocabulary keyed off Python's
 *  ``task_db.models.RINEX_VALIDATION_STATUS_VALUES``. Keep both sides
 *  in sync; tests guard the wire format. */
export type RinexValidationStatus =
  | 'valid'
  | 'subset'
  | 'incompatible'
  | 'validation_failed';

export interface JobDetail extends JobSummary {
  rinex_path: string | null;
  mode: string;
  tectonic_model: string;
  reduction_date: string;
  attempts: number;
  service_task_id: number | null;
  service_uuid: string | null;
  service_state: string | null;
  /** Full error message including ``[permanent]`` etc. */
  error_message: string | null;
  notes: string | null;
  report_txt_path: string | null;
  catalog_csv_path: string | null;
  track_csv_path: string | null;
  /** Phase 3.7: handcrafted IGS-station name lookup (``api.lib.station_names``).
   *  Null when the station code isn't in the curated JSON. */
  station_full_name?: string | null;
  station_country?: string | null;
  /** Phase 3.10: ISO timestamp of the upstream PPP-side delete.
   *  Null means cleanup is pending (sweep will retry) or the job never
   *  reached PPP (no ``service_task_id``). DetailScreen renders the
   *  «🧹 убрано с сервера» mark when this is non-null. */
  service_deleted_at?: string | null;
  /** Phase 3.13.2: per-catalog residual rows. Empty for PENDING / FAILED
   *  jobs and for jobs whose station isn't in the catalogue. DetailScreen
   *  iterates this to decide whether to render the quality-warning plate. */
  residuals: JobResidual[];
  /** Phase 3.15: opaque reason string explaining why the task was
   *  preserved on PPP. Currently always ``"compute_failed"`` when set.
   *  DetailScreen renders a multi-line notice plate explaining what
   *  happened and that cleanup was intentionally skipped. */
  preserve_reason?: string | null;
  /** Phase 4.1: full list of GNSS systems the RINEX header advertises.
   *  May be a SUPERSET of ``gnss_systems`` when the station logs more
   *  constellations than the operator requested. DetailScreen diffs
   *  this against ``gnss_systems`` to print "missing: X, Y" in the
   *  subset plate. */
  actual_gnss_systems?: string[] | null;
  /** Phase 4.7 — substitution provenance.
   *  ``substitution_parent_id`` is set on substitute jobs (created
   *  by the scheduler when the original failed with
   *  ``rinex not available`` in an ``auto_substitute=true`` batch);
   *  it points at the CHAIN ROOT — the original job — so the
   *  Mini App banner can deep-link to it. ``substitution_attempt``
   *  is 0 for originals and 1/2/3 for substitutes (cap=3).
   *  ``substitution_children`` is the inverse — for an ORIGINAL
   *  job that's been substituted, lists every direct substitute
   *  so DetailScreen can render «🔄 Заменена на: #M (KOUR)». */
  substitution_parent_id?: number | null;
  substitution_attempt?: number;
  substitution_children?: SubstitutionLink[];
  /** Phase 4.5 — auto-retry provenance. ``retry_parent_id`` is set
   *  on retry-jobs created by the scheduler when an originally-
   *  requested job hit PlateModelOutsideError; it points at the
   *  CHAIN ROOT (the originally-requested job) so DetailScreen can
   *  deep-link there. ``retry_attempt`` is 0 for root jobs, 1/2/…
   *  for retry depths. ``retry_children`` is the inverse — for the
   *  ROOT job, lists every retry spawned off it; rendered by
   *  DetailScreen as a chain section. ``auto_retry_models`` is the
   *  operator-chosen ordered list this row inherited from its
   *  batch — null when auto-retry was off. */
  retry_parent_id?: number | null;
  retry_attempt?: number;
  retry_children?: RetryChainLink[];
  auto_retry_models?: string[] | null;
  /** Phase 4.8 — captured RINEX header (lines up to ``END OF HEADER``).
   *  Stamped by the scheduler post-fetch for independent verification
   *  of the gfzrnx-based GNSS-systems pre-flight check. NULL for jobs
   *  created before Phase 4.8 or where the extractor failed. The
   *  DetailScreen renders this in a collapsible ``<details>`` block —
   *  default collapsed except for ``rinex_validation_status="incompatible"``
   *  where the operator likely wants to see it first thing. */
  rinex_header_text?: string | null;
  /** Phase 4.6 — opaque tag for *why* this DONE job has no residual.
   *  ``null`` for the normal case (residual exists OR job pre-dates 4.6).
   *  ``"no_igs_reference_within_window"`` — station not in IGS Daily
   *  Combined for obs_date ± 5 days; the fallback was exhausted.
   *  ``"compute_failed_no_coords"`` — PPP report carried no
   *  coords_obs_epoch (BDS-solo / high-latitude case).
   *  DetailScreen renders the matching info-block in place of the
   *  «эталон» card when this is set. */
  residual_skip_reason?: ResidualSkipReason | null;
  /** Phase B Block 1 — PPP solution-quality self-estimate (СКО).
   *  Phase A backfilled these onto 654 DONE jobs; `null` for
   *  failure-block reports and jobs predating Phase A. `sigma_3d_mm`
   *  is √(σx²+σy²+σz²)×1000 — directly comparable with
   *  `residual_3d_mm`. */
  sigma_3d_mm?: number | null;
  sigma_x_m?: number | null;
  sigma_y_m?: number | null;
  sigma_z_m?: number | null;
  sigma_plan_m?: number | null;
  sigma_height_m?: number | null;
  /** Phase B Block 1 — computed flag from
   *  `analytics.aggregator.is_sentinel_sigma`. `true` when the σ
   *  triple is a service stub (identical per-axis triple / >1 m /
   *  missing) — DetailScreen renders «оценка недоступна» instead of
   *  the garbage value. */
  sigma_is_sentinel?: boolean;
  /** Phase H — reference stations of the relative method with
   *  catalog-joined coords. Empty list = no relative-method
   *  references recorded (pre-Phase-A job, or scheduler didn't
   *  capture them); the Mini App hides the «Опорные станции» block
   *  entirely in that case. */
  base_stations?: BaseStationRef[];
  /** Phase H — target station's lat/lon from the catalog, surfaced
   *  on JobDetail so the mini-map doesn't need a second round-trip.
   *  null when station_code isn't in the catalog. */
  station_latitude?: number | null;
  station_longitude?: number | null;
  /** Phase C — convergence-slice metadata. NULL on every non-
   *  convergence job; the Mini App keys the «Тест сходимости» card
   *  on `slice_length_nominal_h !== null`. */
  slice_length_nominal_h?: number | null;
  slice_length_real_h?: number | null;
  /** ISO datetime — slice window start (always 00:00 UTC of obs_date
   *  in the current implementation; reserved for Phase C2). */
  slice_window_start?: string | null;
  slice_window_end?: string | null;
  source_daily_path?: string | null;
}

/** Phase 4.6 — vocabulary keyed off Python's
 *  ``scheduler._residual_compute.SKIP_REASON_*`` constants. Pinned by
 *  the test so both halves stay in sync. */
export type ResidualSkipReason =
  | 'no_igs_reference_within_window'
  | 'compute_failed_no_coords';

/** P1 · 2B — the normalized residual render CLASS. Mirror of Python
 *  ``task_db.residual_state.ResidualDisplay`` values. A cross-language contract
 *  test (``tests/unit/test_residual_display_contract.py``) FAILS if these sets
 *  diverge. We mirror the OUTPUT classes, not the input ``residual_outcome``
 *  vocabulary — a future 7th outcome that maps to an existing class never
 *  touches the frontend. */
export type ResidualDisplay =
  | 'residual'
  | 'absent'
  | 'compute_failed'
  | 'operational_failure'
  | 'unprocessed'
  | 'unknown';

/** Phase H — one reference station of the relative method, joined
 *  against the catalog so the Mini App's mini-map gets lat/lon ready
 *  to plot. `latitude` / `longitude` are `null` for codes absent from
 *  the 770-row pool (Phase H §0.1: ~0.3% of codes, currently just
 *  OPMT). Mini App lists those under the map with «нет координат»
 *  rather than placing a marker. Longitude is pre-wrapped into
 *  Leaflet's `[-180, 180]` band by the backend (same shape as
 *  `MapStation.longitude`). */
export interface BaseStationRef {
  code: string;
  latitude: number | null;
  longitude: number | null;
}

/** Phase 4.7 — minimal pointer to a related job in a substitution
 *  chain. The DetailScreen renders «🔄 Заменена на: #42 (KOUR)»
 *  from this — only the id (for the deep-link) and the station code
 *  (for the operator-readable label) are needed at the call site. */
export interface SubstitutionLink {
  id: number;
  station_code: string | null;
}

/** Phase 4.5 — minimal pointer to a related job in a retry chain.
 *  ``state`` lets DetailScreen tint the link by job state
 *  (in-flight/done/failed) without a second API hit. */
export interface RetryChainLink {
  id: number;
  tectonic_model: string;
  state: string;
}

export interface JobListResponse {
  items: JobSummary[];
  total: number;
  limit: number;
  offset: number;
}

/** Phase 3.14 — aggregated stats per ``Job.tag``. Drives the
 *  "Активные партии" section at the top of the Feed screen. The
 *  ``counts`` dict always carries all five JobState keys (zero-filled
 *  where the state isn't present) so the renderer doesn't need ``?? 0``. */
export interface BatchSummary {
  tag: string;
  /** Spec A · Step 0 — composite legacy key: card identity is
   *  (tag, obs_date, analysis_type). A reused tag now yields SEPARATE
   *  cards per date/type. `obs_date` is null for pre-migration legacy
   *  rows. Optional: an older backend simply omits them. */
  obs_date?: string | null; // ISO date "YYYY-MM-DD"
  analysis_type?: 'daily' | 'convergence';
  /** True when the composite key may still merge two inseparable
   *  legacy runs (created_at span heuristic) — the card is an honest
   *  aggregate with a warning, not one trustworthy run. */
  legacy_ambiguous?: boolean;
  /** False when tag-scoped secondary metrics (accuracy, ETA,
   *  lengths_h, substitution pairs, …) would be contaminated by other
   *  runs sharing the tag ANYWHERE in the DB (not just the current
   *  window). Suppressed metrics arrive as 0 / null / [] — never a
   *  contaminated number. Core counts stay trustworthy. */
  secondary_metrics_reliable?: boolean;
  total_jobs: number;
  counts: Record<JobState, number>;
  /** Jobs in this batch with at least one residual flagged via Phase
   *  3.13.2's data_quality_warning (catalogue-collision case). Separate
   *  from ``counts.failed`` — these jobs completed, just against a
   *  wrong catalogue reference. */
  quality_warnings: number;
  /** ISO UTC — earliest ``created_at`` across the batch. */
  first_created_at: string;
  /** ISO UTC — newest ``COALESCE(finished_at, submitted_at, created_at)``
   *  across the batch. Proxy for "last time any job changed state". */
  last_activity_at: string;
  /** ``(done + failed + cancelled) / total * 100``, one decimal. */
  progress_percent: number;
  /** Mean ``dr_3d_mm`` over DONE residuals on ITRF2014, excluding
   *  ``data_quality_warning=true`` rows. ``null`` when no eligible
   *  residuals exist (yet). */
  avg_done_dr_3d_mm: number | null;
  /** Convenience flag — ``progress_percent >= 100``. */
  is_completed: boolean;
  /** Phase 4.7 — count of substitute jobs in this batch (rows with
   *  ``substitution_parent_id IS NOT NULL``). Rendered as the
   *  «🔄 N» pill on BatchSummaryCard. 0 when auto_substitute was
   *  off or the policy never fired — the pill is hidden in that
   *  case so legacy batches look identical to before. */
  substitution_count?: number;
  /** Phase 4.13 — count of DISTINCT station codes among DONE jobs in
   *  this batch. Operator-facing «how many different stations actually
   *  produced a result?» metric. Drives the «Уникальных DONE-станций:
   *  M из N запрошенных» line in BatchSummaryCard (shown only when the
   *  batch is complete). */
  unique_stations_done?: number;
  /** Wave-5.1 — distinct nominal slice lengths (hours), longest-first. The
   *  «24 12 6 3 1.5 ч» interval row on the Feed convergence card. Empty/absent
   *  for a daily (non-slice) batch. */
  lengths_h?: number[];
  /** Phase 4.13 — count of DISTINCT station codes among the originally-
   *  requested jobs (``substitution_parent_id IS NULL`` rows). Substitute
   *  stations are excluded. Pairs with ``unique_stations_done`` for the
   *  «M из N запрошенных» line. */
  requested_station_count?: number;
  /** Phase 4.13 — total ``len(gnss_systems) × 16`` minutes still ahead
   *  of the scheduler (sum across rows in ``pending`` / ``submitted``
   *  state). The Mini App divides by the PPP parallelism constant (2)
   *  via ``lib/eta_calculator::etaFromSystemMinutes`` to render the
   *  «⏱ примерно завершится через X мин • HH:MM» line. ``0`` for
   *  completed batches — the Mini App hides the ETA row entirely. */
  remaining_system_minutes?: number;
  /** Phase 4.5.2 — server-computed wall-clock minutes until the
   *  batch's remaining work finishes. Reads live
   *  ``SCH_MAX_CONCURRENT_JOBS`` + empirical per-task median, so it
   *  stays accurate across parallelism flips and PPP server
   *  swaps without a frontend rebuild. ``null`` when there's no
   *  remaining work — the Mini App hides the ETA row. Prefer
   *  this field over the legacy ``remaining_system_minutes``-
   *  based calculation when present. */
  eta_minutes_remaining?: number | null;
  /** Phase 4.13.1 — count of DISTINCT new station codes introduced
   *  by the auto-substitute hook (rows with substitution_parent_id
   *  IS NOT NULL). For the "+ N автозамены" segment in
   *  BatchSummaryFeedCard. */
  substituted_station_count?: number;
  /** Phase 4.13.1 — DISTINCT station_code count across the whole
   *  batch (originals + substitutes). For the "= K попробовано"
   *  segment. */
  total_unique_stations?: number;
  /** Phase 4.13.1 — substitution pair details. Empty list when no
   *  substitutions fired. */
  substitution_pairs?: SubstitutionPair[];
  /** Phase 4.13.1 — newest finished_at across the batch. ``null``
   *  when at least one job is still in flight. */
  last_job_finished_at?: string | null;
  /** Phase 6.3 — FAILED-job taxonomy v2. Always six keys
   *  (rinex_real_missing / rinex_parse_error / rinex_not_available /
   *  compute_failed_ambiguity / service_compute_failed / other),
   *  zero-filled when a bucket is empty. Sum equals `counts.failed`.
   *  Mini App hides zero-count rows per spec §A.3. */
  failed_breakdown?: FailedBreakdown;
  /** Phase 6.5 B — aggregate accuracy across DONE-with-residual
   *  jobs in the series. `null` when no such jobs exist — Mini App
   *  hides the ТОЧНОСТЬ block entirely (spec §B.4). */
  accuracy?: SeriesAccuracySummary | null;
  /** Phase 6.5 C — one row per canonical GNSS-config key.
   *  Empty list when no jobs in the series have any config (Mini
   *  App hides ПО КОНФИГУРАЦИЯМ block per spec §C.4). */
  per_config?: SeriesPerConfigBreakdown[];
}

/** Phase 6.3 — FAILED taxonomy v2. String enum keys keyed off the
 *  Python `analytics.failed_classification.FailedCategory` values.
 *  v1 values (`no_systems_in_rinex`, `compute_failed`) are deleted;
 *  Phase 6.3 is a breaking change to the internal API. */
export type FailedCategory =
  | 'rinex_real_missing'
  | 'rinex_parse_error'
  | 'rinex_not_available'
  | 'compute_failed_ambiguity'
  | 'service_compute_failed'
  | 'other';

export type FailedBreakdown = Record<FailedCategory, number>;

/** Phase 6.5 B — aggregate accuracy mirror of the Pydantic schema.
 *  `with_residual === 0` collapses the wrapper to `null` on the
 *  wire (Pydantic-side guard), so callers don't have to inspect
 *  `median_dr_mm === null` to decide whether to render. */
export interface SeriesAccuracySummary {
  total_done: number;
  with_residual: number;
  without_residual: number;
  /** Phase 3.17 D.2 — of `with_residual`, how many used the IGS Daily
   *  SINEX ±5-day neighbour-day fallback. Exact = with_residual − this. */
  fallback_residual: number;
  median_dr_mm: number | null;
  best_dr_mm: number | null;
  best_station: string | null;
  best_config: string | null;
  best_job_id: number | null;
  worst_dr_mm: number | null;
  worst_station: string | null;
  worst_config: string | null;
  worst_job_id: number | null;
}

/** Phase 6.5 C — one row of the per-config table. `config` is the
 *  canonical key (`"gps"`, `"beidou,galileo,glonass,gps"` …);
 *  render via `formatGnssList` for display. */
export interface SeriesPerConfigBreakdown {
  config: string;
  requested: number;
  done_with_residual: number;
  median_dr_mm: number | null;
}

/** Phase 4.13.1 — one (original → new station) substitution pair
 *  inside a batch. Emitted as part of BatchSummary.substitution_pairs
 *  so the Mini App can render "📝 Замены" without per-job
 *  round-trips. */
export interface SubstitutionPair {
  original_code: string;
  new_code: string;
  /** Count of substitute jobs in this pair — typically equals the
   *  number of GNSS configs in the batch (e.g. 5 for a five-config-
   *  per-station batch where the whole station X failed and got
   *  swapped for Y). */
  config_count: number;
}

export interface StatusCounts {
  total: number;
  pending: number;
  submitted: number;
  done: number;
  failed: number;
  cancelled: number;
}

export interface StatusResponse {
  scheduler_running: boolean;
  ppp_online_status: 'ok' | 'down' | 'unknown' | string;
  /** Phase 3.9 — ISO timestamp of the last PPP-Online health probe.
   *  Null when the scheduler hasn't ticked yet (cold start). Mini App
   *  renders a relative «5 мин» freshness label next to the status. */
  ppp_checked_at: string | null;
  counts: StatusCounts;
  last_event_at: string | null;
  /** Reliability Step 3 C3 — per-config adaptive watchdog thresholds in
   *  SECONDS, as last persisted by the scheduler. Null until the first
   *  snapshot (cold start). SettingsScreen renders these as minutes. */
  watchdog_thresholds?: Record<string, number> | null;
}

// ----- UI-side filter key (covers a couple of API states under one label).

/** UI-side filter selection. ``active`` covers ``submitted``. */
export type FilterKey = 'all' | 'pending' | 'active' | 'done' | 'failed';

// ----- Events (Phase 3.3).

export type Severity = 'info' | 'warn' | 'error' | 'success';

export interface EventResponse {
  id: number;
  ts: string; // ISO datetime
  event_type: string;
  severity: Severity;
  job_id: number | null;
  message: string | null;
  /** Packed state transition for state_change events; null otherwise. */
  payload: Record<string, unknown> | null;
}

export interface EventListResponse {
  items: EventResponse[];
  total: number;
  limit: number;
  offset: number;
}

// ----- Event feed («Лента событий») — Phase 6.9 C2.

export type FeedCategory = 'operational' | 'investigatory';
export type FeedSeverity = 'critical' | 'warning' | 'info';

/** One card in the «Лента событий». A card is a single whitelisted
 *  event or a collapsed burst of `count` same-kind events. */
export interface EventFeedCard {
  category: FeedCategory;
  severity: FeedSeverity;
  kind: string;
  ts: string; // ISO datetime — latest event in the group
  message: string | null;
  count: number; // 1, unless this card collapses a burst
  job_id: number | null;
  ids: number[];
}

export interface EventFeedResponse {
  items: EventFeedCard[];
}

// ----- Notification settings («Уведомления по типам») — Phase 6.9 C3.

/** Per-type Telegram notification toggles. App-global (single-user
 *  project). Defaults: per-task start/finish OFF, the rest ON. */
export interface NotificationSettings {
  task_started: boolean;
  task_completed: boolean;
  task_failed: boolean;
  auto_replace: boolean;
  batch_completed: boolean;
}

/** POST body — every field optional (partial update). */
export type NotificationSettingsUpdate = Partial<NotificationSettings>;

// ----- Phase 3.5 (actions / batch / scheduler).

/** Phase 4.5 — operator-visible identifier for one of the 4 PPP
 *  Online plate-tectonic models. Values match the Python enum
 *  ``TectonicModel`` NAMES verbatim so the wire format is
 *  unambiguous in both directions. */
export type TectonicModelCode =
  | 'NNR_MORVEL56'
  | 'NUVEL_1A'
  | 'ITRF2014'
  | 'GRSM_21';

/** Phase 4.5 — operator-readable label for each tectonic model.
 *  Used by BatchModal and DetailScreen to render hyphenated names
 *  instead of the underscore-shaped enum value. */
export const TECTONIC_MODEL_LABELS: Record<TectonicModelCode, string> = {
  NNR_MORVEL56: 'NNR-MORVEL56',
  NUVEL_1A: 'NUVEL-1A',
  ITRF2014: 'ITRF2014',
  GRSM_21: 'GRSM 2.1',
};

export interface BatchCreateRequest {
  dates: string[]; // ISO YYYY-MM-DD; at least one
  station_codes?: string[] | null;
  random_n?: number | null;
  gnss_configs?: string[] | null; // labels: "gps", "gps+glo", "all4", …
  tag: string;
  // Phase 4.7 — opt-in auto-substitute. Only meaningful in random
  // mode (BatchModal hides the checkbox in stations mode). When true,
  // the scheduler clones any job that fails with rinex_not_available
  // onto a different random station from the active pool.
  auto_substitute?: boolean;
  // Phase 4.15 — per-batch cap on the substitution chain depth.
  // Range 1-10, default 5. Only meaningful when auto_substitute=true;
  // BatchModal hides the input otherwise. The scheduler reads the
  // value off each failing job's row (every Job in the batch carries
  // the same stamp) so chained substitutes inherit the original cap.
  max_substitution_attempts?: number;
  // Phase 4.5 — operator-chosen tectonic model. Default NNR_MORVEL56
  // matches PPP-Online's own default (pre-4.5 we hard-coded
  // ITRF2014). Validated against the four-value enum on the backend.
  tectonic_model?: TectonicModelCode;
  // Phase 4.5 — operator-chosen reduction epoch. ISO YYYY-MM-DD;
  // default 2011-01-01 (PPP's standard). The Mini App's date
  // picker writes this verbatim; the API validates the format.
  reduction_date?: string;
  // Phase 4.5 — ordered list of alternative tectonic models to try
  // when ``tectonic_model`` fails plate_outside. ``null`` (the
  // default) means "no auto-retry". The backend validator rejects
  // lists that include the primary ``tectonic_model`` or contain
  // duplicates.
  auto_retry_models?: TectonicModelCode[] | null;
}

export interface BatchCreateResponse {
  job_ids: number[];
  created: number;
  skipped_duplicates: number;
  stations_added: string[];
  tag: string;
  /** Phase 4.14 — subset of ``created`` that landed pre-flight FAILED
   *  because the availability cache flagged the (station, date, model)
   *  tuple as known-bad. Display as "Создано: X (из них Y закешированных
   *  как недоступные)". */
  cached_unavailable_count?: number;
  /** Phase 4.14 — count of stations the random-pool picker dropped
   *  because the cache flagged them for the requested obs_dates+model.
   *  0 (or absent) for explicit-station batches. */
  pool_excluded_unavailable?: number;
  /** «Не применимо» FIX 2 — configs dropped at generation because the
   *  availability cache marked them structurally unreachable (RINEX has
   *  no such system / file on no archive). NOT counted in ``created`` —
   *  no row was made. Shown as «пропущено недоступных: N». */
  unreachable_skipped?: number;
}

/** Phase 4.14 — pre-batch pool inspection. ``GET /pool/availability``. */
export interface PoolAvailabilityResponse {
  total_active: number;
  excluded: number;
  available: number;
  cached_unavailable_codes: string[];
}

export interface SchedulerStatusResponse {
  is_paused: boolean;
  scheduler_running: boolean;
}

// ----- Phase 3.6 — user settings.

export interface UserSettingsResponse {
  notifications_enabled: boolean;
}

export interface UserSettingsUpdateRequest {
  notifications_enabled?: boolean;
}

// ----- Phase 6 — analytics -----

/** Active filter set for the Analytics screen. Both fields are
 *  thread through every analytics endpoint so the three views
 *  refresh atomically when the user flips a filter. */
export interface AnalyticsFilters {
  tectonic_model: string | null;
  /** Canonical config keys (lower, sorted, comma-joined) — empty
   *  list and ``null`` both mean «все конфиги». */
  gnss_configs: string[] | null;
}

export interface AnalyticsBucket {
  /** Russian label, e.g. ``"< 5 мм"``, ``"5–15 мм"``. */
  range: string;
  count: number;
  /** Rounded whole-percent for the bar label. */
  pct: number;
}

export interface AnalyticsGroupHighlight {
  /** Canonical config key OR 4-char station code, depending on
   *  which highlight slot this fills. */
  name: string;
  avg_dr_mm: number;
  count: number;
}

export interface AnalyticsSummary {
  total_residuals: number;
  avg_dr_3d_mm: number;
  min_dr_3d_mm: number;
  max_dr_3d_mm: number;
  buckets: AnalyticsBucket[];
  best_config: AnalyticsGroupHighlight | null;
  worst_config: AnalyticsGroupHighlight | null;
  best_station: AnalyticsGroupHighlight | null;
  worst_station: AnalyticsGroupHighlight | null;
  /** Phase 6.6 C — pointers to the job_id that holds the min/max
   *  ``dr_3d_mm`` value, so the Mini App can render `min` / `max`
   *  numbers as clickable deep-links to `/job/<id>`. All six fields
   *  are `null` when the cohort is empty (mirrors
   *  ``total_residuals === 0``). */
  min_job_id: number | null;
  min_station: string | null;
  min_config: string | null;
  max_job_id: number | null;
  max_station: string | null;
  max_config: string | null;
}

/** Phase 6.6 B — one entry in the «available GNSS configs in the DB»
 *  response. Drives the auto-discovering accordion filter on the
 *  Аналитика screen. */
export interface AnalyticsAvailableConfig {
  /** Canonical lowercase-sorted-comma-joined key — pass back as a
   *  `gnss_configs` filter value verbatim. */
  key: string;
  /** Operator-readable label normalised on the backend
   *  (`"GPS"` / `"все 4"` / `"GPS+ГЛО+GAL"` / …). The frontend
   *  renders this string directly without further translation. */
  label: string;
  /** Job count carrying this exact combo (any state). Sort is
   *  count-DESC / key-ASC on the backend. */
  count: number;
}

export interface AnalyticsAvailableConfigsResponse {
  configs: AnalyticsAvailableConfig[];
}

export interface AnalyticsHeatmapCell {
  station: string;
  config: string;
  /** ``null`` for cells without DONE-with-residual data. Phase 6.3 —
   *  combine with `failed_count` to decide the visual state:
   *    - `avg_dr_mm !== null` ⇒ number cell, colour by gradient
   *    - `avg_dr_mm === null && failed_count > 0` ⇒ «—» with tooltip
   *    - `avg_dr_mm === null && failed_count === 0` ⇒ empty (no «—»)
   */
  avg_dr_mm: number | null;
  count: number;
  /** Phase 6.1 — ``MAX(Job.id)`` across the DONE jobs feeding
   *  this cell's average. Mini App turns a cell click into
   *  ``navigate(/job/<latest_job_id>)`` so the operator lands
   *  on the most recent representative result. ``null`` for
   *  empty cells — those are non-clickable. */
  latest_job_id: number | null;
  /** Phase 6.3 — number of FAILED jobs for this (station, config)
   *  pair. ``> 0`` AND ``avg_dr_mm === null`` ⇒ render «—» cell with
   *  tooltip + drill-down. */
  failed_count?: number;
  /** Phase 6.3 — v2 :class:`FailedCategory` of the most recent FAILED
   *  job for the pair. Drives the dash-cell tooltip wording.
   *  ``null`` when ``failed_count === 0``. */
  last_failed_category?: FailedCategory | null;
  /** Phase 6.3 — ``MAX(Job.id)`` across FAILED rows for the pair.
   *  Mini App turns a dash-cell click into ``navigate(/job/<id>)`` so
   *  the operator lands on a representative FAILED DetailScreen. */
  last_failed_job_id?: number | null;
  /** Phase G.2 — DONE jobs whose residual was skipped with
   *  ``compute_failed_no_coords`` (PPP returned no coords). Drives
   *  the ✗ «нет решения» glyph. */
  skip_compute_failed_count?: number;
  /** Phase G.2 — DONE jobs whose residual was skipped with
   *  ``no_igs_reference_within_window`` (no IGS Daily entry in ±5
   *  days). Drives the ○ «нет эталона» glyph. */
  skip_no_reference_count?: number;
  /** P1 · 2B — DONE jobs whose residual outcome is an OPERATIONAL failure
   *  (``failed_operational`` / ``retry_pending`` — 401 / timeout / etc). A
   *  SEPARATE, VISIBLE technical glyph (⚡): NOT «нет эталона» (○) and NOT an
   *  empty «не пытались» cell. ``0`` during DEPLOY 1 (writer ships in DEPLOY 2). */
  skip_operational_failure_count?: number;
  /** P1 · 2B — DONE jobs whose residual outcome did not map to a known class
   *  (a future value). Kept SEPARATE from operational; visible, out of science. */
  skip_unknown_count?: number;
  /** P1 · 2B — rows carrying a residual AND an outcome that denies it: a
   *  data-consistency diagnostic counter (never «нет эталона»). */
  skip_invariant_violation_count?: number;
  /** Phase G.2 — ``MAX(Job.id)`` across DONE-with-skip rows.
   *  Mini App turns ✗/○ click into ``navigate(/job/<id>)``. */
  last_skip_job_id?: number | null;
}

export interface AnalyticsHeatmap {
  /** Pre-sorted best-first by per-station mean dR. */
  stations: string[];
  /** Canonical column order + any data-driven extras appended. */
  configs: string[];
  /** Flat row-major list of ``stations × configs`` entries. */
  cells: AnalyticsHeatmapCell[];
}

export interface AnalyticsScatterPoint {
  station: string;
  latitude_deg: number;
  longitude_deg: number;
  config: string;
  dr_3d_mm: number;
  tectonic_model: string;
  /** Phase 6.1 — source job id (drives tooltip click → DetailScreen). */
  job_id: number;
  /** Phase 6.1 — ISO ``YYYY-MM-DD``; drives the tooltip's
   *  «<obs_date> · <tectonic_model>» second line. */
  obs_date: string;
}

export interface AnalyticsScatterResponse {
  points: AnalyticsScatterPoint[];
}

// ----- Phase B — processing time + self-assessment -----

/** Phase I — one calendar-day bucket of the activity heatmap.
 *
 *  Three counters land on each day so the click-popup can render
 *  «Всего N · DONE M · FAILED K» without a follow-up call. Other
 *  states (SUBMITTED/PENDING/CANCELLED) ride silently in
 *  `total - done - failed`. */
export interface ActivityDay {
  /** ISO YYYY-MM-DD. */
  date: string;
  total: number;
  done: number;
  failed: number;
}

/** Phase I — payload of `GET /api/v1/analytics/activity`.
 *
 *  Two parallel date-axis aggregations:
 *  * `by_completion` — DATE(finished_at), operator's production
 *    heatmap («сколько прогнал N мая»). Excludes NULL finished_at.
 *  * `by_observation` — obs_date from RINEX-name. Shows epoch
 *    coverage of the data tested.
 *
 *  Dates with zero jobs are NOT in the payload — the Mini App
 *  fills the GitHub grid bleach for missing dates between the
 *  first date and today. */
export interface AnalyticsActivityResponse {
  by_completion: ActivityDay[];
  by_observation: ActivityDay[];
}

/** Phase B Block 2 — one row of «время обработки по конфигам».
 *  `config` is the canonical GNSS key — render via `formatConfig`.
 *  All time fields are in minutes. */
export interface ProcessingTimeConfigRow {
  config: string;
  n: number;
  median_min: number;
  mean_min: number;
  max_min: number;
}

/** Phase B Block 2 — payload of `GET /analytics/processing-time`.
 *  Rows arrive sorted by median ASC (fastest configs first). */
export interface ProcessingTimeResponse {
  processing_time_by_config: ProcessingTimeConfigRow[];
}

/** Phase B Block 3 — one «слепое пятно»: a job where the service's σ
 *  badly under-states the measured |dR|. `ratio` = dr / σ. */
export interface SelfAssessmentBlindSpot {
  job_id: number;
  station: string;
  config: string;
  dr_3d_mm: number;
  sigma_3d_mm: number;
  ratio: number;
}

/** Phase B Block 3 — payload of `GET /analytics/self-assessment`.
 *  Computed over the strict-clean cohort with a non-sentinel σ;
 *  `sentinel_excluded` is how many rows the sentinel filter dropped.
 *  `median_ratio` / `spearman` are `null` only when `cohort === 0`. */
export interface SelfAssessmentResponse {
  cohort: number;
  sentinel_excluded: number;
  overestimate_pct: number;
  conservative_pct: number;
  median_ratio: number | null;
  spearman: number | null;
  blind_spots: SelfAssessmentBlindSpot[];
}

/** Translate the UI filter back into the API ``state`` parameter. */
export function filterToApiStates(filter: FilterKey): JobState[] | undefined {
  switch (filter) {
    case 'all':
      return undefined;
    case 'pending':
      return ['pending'];
    case 'active':
      return ['submitted'];
    case 'done':
      return ['done'];
    case 'failed':
      return ['failed'];
  }
}


// ----- Phase 6.3 — backfill plan / run -----

/** Phase 6.3 — source selector for BatchModal «Восполнение» mode.
 *  Mirrors the Python BackfillSourceRequest. Exactly one of (tags,
 *  station_codes) carries meaning depending on `source_type`; the
 *  third type (`all_dates_with_done`) ignores both. */
export interface BackfillSourceRequest {
  source_type: 'all_dates_with_done' | 'specific_tags' | 'station_codes';
  tags?: string[];
  station_codes?: string[];
  date_from?: string | null;
  date_to?: string | null;
}

/** Phase 6.3 — POST body for `/api/v1/series/plan-backfill`. */
export interface BackfillPlanRequest {
  source: BackfillSourceRequest;
  target_configs: string[];
  include_failed_categories: FailedCategory[];
  tectonic_model?: string | null;
}

/** Phase 6.3 — one preview task. The full list lives server-side; the
 *  response carries only the first 10 for the modal preview pane. */
export interface BackfillPlannedTask {
  station_code: string;
  obs_date: string;
  config: string;
}

/** Phase 6.3 — read-only preview response from
 *  `/api/v1/series/plan-backfill`.
 *
 *  `breakdown_by_reason_for_excluded` is a sparse dict keyed by the
 *  exclusion reason (`already_done` / `cached_unavailable` / the
 *  deterministic FailedCategory values that can't be retried / the
 *  v2 FailedCategory values the operator didn't opt-in for). */
export interface BackfillPlanResponse {
  total_planned_tasks: number;
  covered_pairs: number;
  covered_dates: number;
  breakdown_by_reason_for_excluded: Record<string, number>;
  estimated_duration_min: number;
  preview_first_10: BackfillPlannedTask[];
  is_large_plan: boolean;
}

/** Phase 6.3 — POST body for `/api/v1/series/run-backfill`. Extends
 *  the plan request with the operator-chosen tag + a confirm flag. */
export interface BackfillRunRequest extends BackfillPlanRequest {
  tag: string;
  confirm: boolean;
}

/** Phase 6.3 — response from `/api/v1/series/run-backfill`. Same
 *  shape as `BatchCreateResponse` so the existing post-create
 *  plumbing (toast, navigation) handles both without branching. */
export interface BackfillRunResponse {
  job_ids: number[];
  created: number;
  tag: string;
}

/** Phase G.1 — per-config breakdown for one map station.
 *
 *  Three render branches when a single config X is selected on the
 *  map (see `markerColor` in `StationMarker.tsx`):
 *    * `median_dr_mm !== null` → цветной маркер (drColor шкала).
 *    * `had_attempt && median_dr_mm === null` → чёрный (пробовали,
 *      strict-clean residual не получилась — compute_failed / нет эталона).
 *    * `!had_attempt` → серый (этот конфиг здесь не запускали). */
export interface MapStationPerConfig {
  config: string;
  job_count: number;
  had_attempt: boolean;
  median_dr_mm: number | null;
}

/** Phase 5 — one station entry on the map.
 *
 *  Coordinates are guaranteed in Leaflet's `[-90, 90]` / `[-180, 180]`
 *  range — the backend normalises SINEX 0-360 longitudes at the
 *  endpoint layer (see `analytics/map._normalise_longitude`).
 *
 *  Aggregates (`total_jobs` + metrics) apply the Phase 6.3.1 strict
 *  cohort filter, so a station with only polluted DONE rows shows
 *  `done_with_residual=0` and `median_dr_mm=null`.
 *
 *  Phase G.1 — `per_config` carries the per-config attempt+median
 *  breakdown that drives «карта реагирует на конфиг по-настоящему».
 *  Empty list for sleeping stations. */
export interface MapStation {
  code: string;
  latitude: number;
  longitude: number;
  country_code: string | null;
  description: string | null;
  city: string | null;
  total_jobs: number;
  done_with_residual: number;
  median_dr_mm: number | null;
  best_dr_mm: number | null;
  best_job_id: number | null;
  best_config: string | null;
  /** ISO-8601 timestamp string; `null` when the station has no jobs. */
  last_activity_at: string | null;
  active_now: boolean;
  per_config: MapStationPerConfig[];
}

/** Phase 5 — payload of `GET /api/v1/map/stations`. */
export interface MapStationsResponse {
  stations: MapStation[];
  total: number;
  with_jobs: number;
  active_now: number;
}

// ----------------------------------------------------------------- Phase C-ui

/** POST body for `/api/v1/series/run-convergence-async`. The backend
 *  validates `obs_date` is in the past, every length is in (0, 24],
 *  and stations resolve in the catalogue. One submit produces one
 *  series per station, built in the background by the ConvergencePrep
 *  daemon (the synchronous `/series/run-convergence` was removed in
 *  Wave 4c).
 *
 *  Phase C-ui-v2 — XOR between `stations` and `random_n`. The
 *  backend's Pydantic model rejects «both» and «neither» with a
 *  422; the form is responsible for sending exactly one. */
export interface ConvergenceRunRequest {
  stations?: string[];
  random_n?: number;
  auto_substitute?: boolean;
  obs_date: string;
  gnss_configs: string[];
  lengths_hours: number[];
  tectonic_model?: string;
  reduction_date?: string;
}

// ------------------------------------------------------- Wave 4b async-create

/** 202 body of `POST /api/v1/series/run-convergence-async`. The SINEX
 *  gate ran synchronously (cheap), so `viable_count` /
 *  `skipped_unavailable_stations` / `note` are already known; the slow
 *  fetch+slice+create runs in the background. Poll
 *  {@link ConvergencePrepStatus} via `GET /series/prep/{batch_id}`. */
export interface ConvergencePrepAccepted {
  batch_id: string;
  /** How many stations were enqueued for prep (== `viable_count`). */
  total: number;
  viable_count: number;
  skipped_unavailable_stations: string[];
  note: string | null;
  /** Honest input→accepted funnel (defect fix 2026-06-14). Optional so older
   *  fixtures/mocks that predate the funnel still type-check; real 202s always
   *  carry them. `requested_count` is the backend-received count (random_n is
   *  client-capped before the call — the form renders that cap line itself). */
  requested_count?: number;
  resolved_count?: number;
  excluded_unavailable_count?: number;
}

/** One prepared station in the progress view (DONE prep row + its jobs). */
export interface ConvergencePrepSeriesItem {
  station_code: string;
  tag: string;
  job_ids: number[];
}

/** One station the prep daemon couldn't prepare (fetch/slice failed). */
export interface ConvergencePrepFailureItem {
  station_code: string;
  reason: string;
}

/** Progress of an async convergence batch, for the «готовлю N/M» UI.
 *  `status` is `PREPARING` while any row is QUEUED/RUNNING, else `DONE`
 *  (all prepared) or `PARTIAL` (some FAILED). */
export interface ConvergencePrepStatus {
  batch_id: string;
  total: number;
  prepared: number;
  failed: number;
  queued: number;
  running: number;
  status: 'PREPARING' | 'DONE' | 'PARTIAL';
  series: ConvergencePrepSeriesItem[];
  failures: ConvergencePrepFailureItem[];
}

/** One row in the convergence-series list. Configs the operator
 *  picked at create-time are reconstructed from the jobs'
 *  `gnss_systems` field — a tag may carry several configs, each
 *  drawing its own curve. */
export interface ConvergenceSeriesListItem {
  tag: string;
  station_code: string;
  obs_date: string;
  gnss_configs: string[];
  /** Wave-5.1 — distinct nominal slice lengths (hours), longest-first (the
   *  «24 12 6 3 1.5 ч» interval row). */
  lengths_h?: number[];
  slice_count: number;
  done_count: number;
  failed_count: number;
}

export interface ConvergenceSeriesListResponse {
  series: ConvergenceSeriesListItem[];
}

/** One point on the convergence curve. `dr_3d_mm` is the primary
 *  metric (residual vs IGS Daily SINEX); `sigma_3d_mm` is the
 *  service's σ self-estimate — shown side-by-side so operators see
 *  the σ ≠ accuracy distinction (Phase B insight). */
export interface ConvergenceCurvePoint {
  length_nominal_h: number;
  length_real_h: number;
  job_id: number;
  state: string;
  dr_3d_mm: number | null;
  sigma_3d_mm: number | null;
  processing_seconds: number | null;
  no_solution: boolean;
  error_summary: string | null;
}

export interface ConvergenceCurveResponse {
  tag: string;
  station_code: string;
  obs_date: string;
  gnss_configs: string[];
  points: ConvergenceCurvePoint[];
}

/** Phase C-agg — one (config × nominal-length) cell of the aggregated
 *  convergence curve. `median_dr_mm` is the plotted value; `p25`/`p75`
 *  carry the 25–75 spread band (shipped now, rendered in a later phase). */
export interface ConvergenceAggregatePoint {
  length_nominal_h: number;
  median_dr_mm: number;
  p25_dr_mm: number;
  p75_dr_mm: number;
  n: number;
}

/** Median |dR| curve for one config (`gps`/`glo`/`gal`/`bds`/`all4`/a
 *  multi-system label), nominal-length ascending. */
export interface ConvergenceAggregateConfigCurve {
  config: string;
  points: ConvergenceAggregatePoint[];
}

/** Honest series partition — `with_result` / `no_reference` /
 *  `not_computed` sum to `series`. Never mix «без эталона» with measured
 *  accuracy. */
export interface ConvergenceAggregateSummary {
  series: number;
  stations: number;
  with_result: number;
  no_reference: number;
  /** Wave-5.1 — series with PENDING/SUBMITTED slices and no clean point yet
   *  («⏳ в работе»), split out from genuine `not_computed` failures. */
  in_progress: number;
  not_computed: number;
  latest_obs_date: string | null;
}

/** Response of `GET /api/v1/analytics/convergence/aggregate` — the
 *  «Сходимость» tab hero. `lengths_h` is the curve's X grid (sorted
 *  union of nominal lengths present); each config's `points` align to a
 *  subset of it. Time-to-threshold is derived client-side. */
export interface ConvergenceAggregateResponse {
  lengths_h: number[];
  configs: ConvergenceAggregateConfigCurve[];
  summary: ConvergenceAggregateSummary;
}

/** One station×config cell of the «Сходимость» matrix
 *  (`GET /api/v1/analytics/convergence/heatmap`). `state`:
 *  - `"curve"` — `curve` holds the median |dR| (mm) per length in
 *    {@link ConvergenceHeatmapData.lengths} (`null` where that length has no
 *    strict-clean point for this station×config);
 *  - `"noref"` — DONE slices but no clean point («○ без эталона»);
 *  - `"fail"`  — slices exist but none DONE («✗ не вычислено»).
 *  Untested (empty) pairs are omitted from `cells`. */
export interface ConvergenceHeatmapCell {
  station: string;
  config: string;
  state: 'curve' | 'noref' | 'fail';
  curve?: (number | null)[] | null;
}

/** Response of `GET /api/v1/analytics/convergence/heatmap` — the station×config
 *  matrix. `lengths` is the shared nominal grid every `cells[].curve` aligns
 *  to. The frontend derives BOTH the hours-to-threshold matrix and the
 *  time-to-threshold histogram from these per-cell curves client-side (the
 *  threshold stays interactive), so there's no separate histogram endpoint. */
export interface ConvergenceHeatmapData {
  stations: string[];
  configs: string[];
  lengths: number[];
  cells: ConvergenceHeatmapCell[];
}

// ----------------------------------------------------------------- Phase R

/** Response of `GET /api/v1/stats/failure-breakdown`.
 *
 *  Total is `done + pending + submitted + cancelled + failed_total`.
 *  The five Phase R counters sum to `failed_total - uncategorised`
 *  (each FAILED row is in exactly one bucket; `uncategorised` is
 *  the count of terminal rows still NULL on `failure_category` —
 *  pre-backfill, decreases to zero once the script runs).
 *  `no_reference` here counts DONE-without-residual specifically —
 *  it's NOT part of `failed_total`, it's a separate slice of DONE. */
export interface FailureBreakdownResponse {
  total: number;
  done: number;
  pending: number;
  submitted: number;
  cancelled: number;
  failed_total: number;
  not_applicable: number;
  service_failed: number;
  no_reference: number;
  file_unavailable: number;
  other: number;
  uncategorised: number;
}
